import React, { useState } from 'react';
import { Shield, FileText, ArrowRight, AlertTriangle } from 'lucide-react';

// Calculate Forensic Grade based on financial metrics
export const calculateForensicGrade = (values) => {
  const { homeValue, mortgageBalance, shadowDebt, householdIncome } = values;
  
  // Calculate key ratios
  const ltv = (mortgageBalance / homeValue) * 100;
  const dti = ((mortgageBalance + shadowDebt) / householdIncome) * 100;
  const shadowDebtRatio = (shadowDebt / homeValue) * 100;
  
  // Score calculation (lower is better)
  let score = 0;
  
  // LTV scoring (0-35 points)
  if (ltv < 65) score += 0;
  else if (ltv < 75) score += 10;
  else if (ltv < 85) score += 20;
  else if (ltv < 95) score += 30;
  else score += 35;
  
  // DTI scoring (0-35 points)
  if (dti < 200) score += 0;
  else if (dti < 350) score += 10;
  else if (dti < 500) score += 20;
  else if (dti < 650) score += 30;
  else score += 35;
  
  // Shadow debt scoring (0-30 points)
  if (shadowDebtRatio < 2) score += 0;
  else if (shadowDebtRatio < 5) score += 10;
  else if (shadowDebtRatio < 8) score += 20;
  else score += 30;
  
  // Map score to grade
  if (score <= 10) return { grade: 'A', color: 'text-emerald-600', bgColor: 'bg-emerald-100' };
  if (score <= 25) return { grade: 'B+', color: 'text-emerald-500', bgColor: 'bg-emerald-50' };
  if (score <= 40) return { grade: 'B', color: 'text-teal-500', bgColor: 'bg-teal-50' };
  if (score <= 55) return { grade: 'C+', color: 'text-amber-500', bgColor: 'bg-amber-50' };
  if (score <= 70) return { grade: 'C', color: 'text-amber-600', bgColor: 'bg-amber-100' };
  if (score <= 85) return { grade: 'C-', color: 'text-orange-500', bgColor: 'bg-orange-100' };
  return { grade: 'D', color: 'text-red-500', bgColor: 'bg-red-100' };
};

export const ForensicGradePopup = ({ 
  values, 
  interestLeak, 
  onSubmit, 
  isLoading 
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  
  const gradeInfo = calculateForensicGrade(values);
  const monthlyLeak = Math.round(interestLeak / 12);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }
    
    onSubmit({ email });
  };

  return (
    <div 
      className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-w-lg mx-auto"
      data-testid="forensic-grade-popup"
    >
      {/* Grade Header */}
      <div className={`${gradeInfo.bgColor} p-6 text-center border-b border-slate-200`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <AlertTriangle className={`w-5 h-5 ${gradeInfo.color}`} />
          <span className="text-sm uppercase tracking-widest font-heading font-semibold text-slate-600">
            Your Liquidity Grade
          </span>
        </div>
        <div className={`text-6xl md:text-7xl font-heading font-bold ${gradeInfo.color}`}>
          {gradeInfo.grade}
        </div>
      </div>

      {/* Warning Message */}
      <div className="p-6 md:p-8">
        <p className="text-lg text-navy-950 leading-relaxed mb-6">
          Your <span className="font-semibold text-red-500">"Shadow Debt"</span> is currently eroding your home equity. 
          You are paying approximately <span className="font-bold text-red-500">${monthlyLeak.toLocaleString()}/month</span> in 
          avoidable interest.
        </p>

        {/* Lead Magnet */}
        <div className="bg-slate-50 rounded-xl p-5 mb-6 border border-slate-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-gold-400/10 rounded-lg">
              <FileText className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-navy-950 mb-1">
                Free 5-Page Forensic Wealth Report
              </h3>
              <p className="text-sm text-slate-600">
                See the exact restructure plan to plug the leak and recover your monthly cash flow.
              </p>
            </div>
          </div>
        </div>

        {/* Email Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              data-testid="forensic-email-input"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              className={`w-full bg-white border-2 ${error ? 'border-red-400' : 'border-slate-200'} focus:border-gold-400 text-navy-950 rounded-xl px-5 py-4 text-lg outline-none transition-colors placeholder:text-slate-400`}
            />
            {error && (
              <p className="text-red-500 text-sm mt-1">{error}</p>
            )}
          </div>

          <button
            type="submit"
            data-testid="send-report-btn"
            disabled={isLoading}
            className="w-full bg-gold-400 text-navy-950 hover:bg-gold-500 font-heading font-semibold text-lg px-8 py-5 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-navy-950 border-t-transparent rounded-full animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                Send My Report
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        {/* Trust Badge */}
        <div className="flex items-center justify-center gap-2 mt-6 text-slate-500 text-sm">
          <Shield className="w-4 h-4" />
          <span>No SIN or credit check required</span>
        </div>
      </div>
    </div>
  );
};
